import {useState, useEffect, useRef} from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import {Typography} from '@mui/material'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {useMobile} from '../../../../hooks'
import {scheduleApi} from '../../../../services'
import {useParams} from 'react-router-dom'
import {parseError, formatTime} from '../../../../utils/helpers'
import {TextSkeleton, skeletonBusyProps} from '../../../../components'

// Placeholder blocks at typical visit times for each day of the requested range, shown while visits load.
const placeholderEvents = (startDate, endDate) => {
    const slots = [
        ['09:00', '10:30'],
        ['13:00', '14:00'],
        ['11:00', '12:30'],
        ['15:00', '16:30'],
    ]
    const days = Math.min(Math.max((endDate?.diff(startDate, 'day') ?? 0) + 1, 1), 42)
    return Array.from({length: days}).flatMap((_, index) => {
        const date = startDate?.add(index, 'day').format('YYYY-MM-DD')
        return [slots[index % 2], slots[2 + (index % 2)]].map(([start, end], slot) => ({
            id: `placeholder-${index}-${slot}`,
            start: `${date}T${start}`,
            end: `${date}T${end}`,
            placeholder: true,
            backgroundColor: '#f7faf7',
            borderColor: '#f7faf7',
        }))
    })
}

export default ({search, statuses, selectedStatuses, startDate, endDate, calendarView, setVisitStats}) => {
    const {setMessageBox, branchId} = useGlobalStates()
    const {caregiverId} = useParams()
    const {mobile} = useMobile()
    const [events, setEvents] = useState([])
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const loadedQuery = useRef(null)

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (calendarView) {
                loadData()
            }
        }, 1000)
        return () => clearTimeout(timeout)
    }, [branchId, search, selectedStatuses, startDate, endDate, mobile, calendarView])

    const loadData = () => {
        const params = {
            page: 1,
            page_size: 100,
            search: search,
            sort_by: null,
            sort_order: null,
            status: selectedStatuses?.length === statuses?.length ? null : selectedStatuses?.join(','),
            caregiver_ids: caregiverId,
            date_from: startDate?.format('YYYY-MM-DD'),
            date_to: endDate?.format('YYYY-MM-DD'),
        }
        const query = JSON.stringify([branchId, params])
        const id = ++requestId.current
        // Switching back to the calendar with unchanged filters refreshes the visible events in place.
        if (query !== loadedQuery.current) {
            setSkeleton(true)
        }
        scheduleApi
            .getCaregiverVisits(branchId, caregiverId, params)
            .then(({data: {visits, status_counts}}) => {
                if (id !== requestId.current) return
                loadedQuery.current = query
                setEvents(
                    visits?.map(item => ({
                        ...item,
                        start: item?.date?.slice(0, 11) + item?.start_time,
                        end: item?.date?.slice(0, 11) + item?.end_time,
                    })),
                )
                setVisitStats(status_counts)
                setSkeleton(false)
            })
            .catch(error => {
                if (id !== requestId.current) return
                setVisitStats(prev => prev || {})
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
                setSkeleton(false)
            })
    }

    const renderEventContent = eventInfo => {
        if (eventInfo?.event?.extendedProps?.placeholder) {
            return (
                <div style={{display: 'flex', flexDirection: 'column', gap: 6, padding: 6, backgroundColor: '#f7faf7'}}>
                    <TextSkeleton width="80%" fontSize={12} />
                    <TextSkeleton width="60%" fontSize={12} />
                    <TextSkeleton width="50%" fontSize={8} />
                </div>
            )
        }
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: 6, padding: 6, backgroundColor: '#f7faf7'}}>
                <Typography variant="h10" color="#556950">
                    {eventInfo?.event?.extendedProps?.schedule?.service?.name}
                </Typography>
                <Typography variant="body3" color="text.secondary">
                    {eventInfo?.event?.extendedProps?.schedule?.client?.full_name}
                </Typography>
                <Typography variant="body3" color="#99BD90" style={{fontSize: 8}}>
                    {formatTime(eventInfo?.event?.extendedProps?.start_time)}-{formatTime(eventInfo?.event?.extendedProps?.end_time)}
                </Typography>
            </div>
        )
    }

    // Not a SkeletonContainer: swapping the wrapper type would remount the calendar and reset its view.

    return (
        <div {...skeletonBusyProps(skeleton)}>
            <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="timeGridWeek"
                headerToolbar={{
                    left: 'prev,next,today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay',
                }}
                events={skeleton ? placeholderEvents(startDate, endDate) : events}
                selectable={false}
                editable={false}
                allDaySlot={false}
                eventContent={renderEventContent}
            />
        </div>
    )
}
